import {
  ComplexDateOutputValue,
  TIME_DIRECTIONS,
  TIME_GRANULARITIES,
  TimeDirection,
  TimeGranularity,
} from '@/components/ui/daisy/templates/complex-date-input/types';
import { Nullable } from '@/models/Model';
import { getIsoDateParam, getNumberParam } from '@/utils/filters/searchParams';

export type DateRangeFilterParams = {
  dateRangeAfter?: string;
  dateRangeBefore?: string;
  dateRangeDirection?: TimeDirection;
  dateRangeGranularity?: TimeGranularity;
  dateRangeAmount?: number;
};

const CLEARED_DATE_RANGE_PARAMS: DateRangeFilterParams = {
  dateRangeAfter: undefined,
  dateRangeBefore: undefined,
  dateRangeDirection: undefined,
  dateRangeGranularity: undefined,
  dateRangeAmount: undefined,
};

const isTimeDirection = (value: string | null): value is TimeDirection =>
  TIME_DIRECTIONS.includes(value as TimeDirection);

const isTimeGranularity = (value: string | null): value is TimeGranularity =>
  TIME_GRANULARITIES.includes(value as TimeGranularity);

const getDiscreteParams = (
  params: URLSearchParams,
  hasDateRange: boolean,
): Pick<
  DateRangeFilterParams,
  'dateRangeDirection' | 'dateRangeGranularity' | 'dateRangeAmount'
> => {
  const direction = params.get('dateRangeDirection');
  const granularity = params.get('dateRangeGranularity');
  const amount = getNumberParam(params, 'dateRangeAmount');

  if (
    !hasDateRange ||
    !isTimeDirection(direction) ||
    !isTimeGranularity(granularity) ||
    (direction !== 'this' && !amount)
  ) {
    return {};
  }

  return {
    dateRangeDirection: direction,
    dateRangeGranularity: granularity,
    dateRangeAmount: direction === 'this' ? undefined : amount,
  };
};

export const parseDateRangeParams = (
  params: URLSearchParams,
): DateRangeFilterParams => {
  const dateRangeAfter = getIsoDateParam(params, 'dateRangeAfter');
  const dateRangeBefore = getIsoDateParam(params, 'dateRangeBefore');

  return {
    dateRangeAfter,
    dateRangeBefore,
    ...getDiscreteParams(params, !!dateRangeAfter && !!dateRangeBefore),
  };
};

export const toComplexDateValue = ({
  dateRangeAfter,
  dateRangeBefore,
  dateRangeDirection,
  dateRangeGranularity,
  dateRangeAmount,
}: DateRangeFilterParams): Nullable<ComplexDateOutputValue> => {
  if (!dateRangeAfter && !dateRangeBefore) return null;
  if (
    dateRangeDirection &&
    dateRangeGranularity &&
    dateRangeAfter &&
    dateRangeBefore
  ) {
    return {
      type: 'discrete',
      direction: dateRangeDirection,
      granularity: dateRangeGranularity,
      amount: dateRangeAmount,
      dateFrom: dateRangeAfter,
      dateTo: dateRangeBefore,
    };
  }
  if (dateRangeAfter && dateRangeAfter === dateRangeBefore) {
    return { type: 'single', date: dateRangeAfter };
  }
  return {
    type: 'range',
    dateFrom: dateRangeAfter ?? '',
    dateTo: dateRangeBefore ?? '',
  };
};

export const fromComplexDateValue = (
  value: Nullable<ComplexDateOutputValue>,
): DateRangeFilterParams => {
  if (!value) return CLEARED_DATE_RANGE_PARAMS;
  if (value.type === 'discrete') {
    return {
      dateRangeAfter: value.dateFrom,
      dateRangeBefore: value.dateTo,
      dateRangeDirection: value.direction,
      dateRangeGranularity: value.granularity,
      dateRangeAmount: value.amount,
    };
  }
  if (value.type === 'single') {
    return {
      ...CLEARED_DATE_RANGE_PARAMS,
      dateRangeAfter: value.date,
      dateRangeBefore: value.date,
    };
  }
  return {
    ...CLEARED_DATE_RANGE_PARAMS,
    dateRangeAfter: value.dateFrom || undefined,
    dateRangeBefore: value.dateTo || undefined,
  };
};
